import React, { useCallback, useEffect, useState } from "react";
import { Box, Text } from "@mantine/core";
import "./PlayerContainer.css";
import JoinGame from "./playerComponents/JoinGame";
import { DEFAULT_THEME } from "../shared/util/consts";
import LoadingSpinner from "../shared/components/LoadingSpinner";
import { GTVMessage } from "../shared/util/types";
import MultipleChoiceButtons, {
  MultipleChoiceButtonInterface,
} from "./playerComponents/MultipleChoiceButtons";
import SubmitText from "./playerComponents/SubmitText";
import { Link } from "react-router-dom";
import { useAuth } from "../shared/contexts/AuthContext";
import { useWebSocket } from "../shared/hooks/WebSocketHook";
import { useCastSender } from "../shared/hooks/CastSenderHook";
import MultipleChoiceMultipleSelect from "./playerComponents/MultipleChoiceMultipleSelect";

const PlayerContainer: React.FC = () => {
  const { isAuthenticated, logout } = useAuth();
  const [theme, setTheme] = useState<any>(DEFAULT_THEME);
  const [sessionId, setSessionId] = useState<string>("");
  const [playerName, setPlayerName] = useState<string>("");
  const [roomCode, setRoomCode] = useState<string>("");
  const [playerMessage, setPlayerMessage] = useState<string>("Loading...");
  const [payload, setPayload] = useState<any>(null);
  const instructions = payload?.Instructions;
  const subInstructions = payload?.SubInstructions ?? "";
  const [logoVisible, setLogoVisible] = useState<boolean>(true);
  const [state, setState] = useState<string>("JoinGame");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [multipleChoiceData, setMultipleChoiceData] = useState<
    MultipleChoiceButtonInterface[]
  >([]);
  const handleMessage = useCallback(
    (msg: GTVMessage) => {
      try {
        console.log("Player Processing msg", msg);
        setIsLoading(false);
        setLogoVisible(false);
        switch (msg.Type) {
          case "MultipleChoice":
          case "MultipleChoiceMultipleSelect":
            setState(msg.Type);
            setPayload(msg.Payload);
            setMultipleChoiceData(
              msg.Payload.Choices as MultipleChoiceButtonInterface[],
            );
            break;
          case "SubmitText":
            setState(msg.Type);
            setPayload(msg.Payload);
            break;
          case "JoinGame":
            setState("JoinGame");
            break;
          case "Theme":
            setTheme(msg.Payload);
            break;
          case "SessionId":
            setSessionId(msg.Payload);
            break;
          case "Waiting":
            setState(msg.Type);
            setPayload(msg.Payload);
            break;
          case "Error":
            setPlayerMessage(msg.Payload);
            break;
          default:
            break;
        }
      } catch (err) {
        console.error(err);
        if (typeof err === "string") {
          setPlayerMessage((err as string).toString());
        }
      }
    },
    [setPlayerMessage],
  );
  const { sendSocketMessage, socketConnected: isConnected } =
    useWebSocket(handleMessage);
  const getMultipleChoiceComponents = () => {
    return (
      <MultipleChoiceButtons
        theme={theme}
        instructions={instructions}
        subInstructions={subInstructions}
        options={multipleChoiceData}
        onSelect={(option: string) => {
          // Don't do anything if no response message is set
          if (
            !payload?.ResponseMessage ||
            payload?.ResponseMessage.length === 0
          ) {
            return;
          }
          setPlayerMessage("");
          sendSocketMessage({
            Type: payload?.ResponseMessage ?? "",
            SessionId: sessionId,
            RoomCode: roomCode,
            Payload: option,
          });
          setIsLoading(true);
        }}
      />
    );
  };
  const getMultipleChoiceMultipleSelectComponents = () => {
    return (
      <MultipleChoiceMultipleSelect
        theme={theme}
        instructions={instructions}
        subInstructions={subInstructions}
        options={multipleChoiceData}
        onSubmit={(selectedOptions: string[]) => {
          if (
            !payload?.ResponseMessage ||
            payload?.ResponseMessage.length === 0
          ) {
            return;
          }
          setPlayerMessage("");
          sendSocketMessage({
            Type: payload?.ResponseMessage ?? "",
            SessionId: sessionId,
            RoomCode: roomCode,
            Payload: selectedOptions,
          });
          setIsLoading(true);
        }}
        submitButtonText={
          payload?.SubmitButtonText ?? "Submit Selected Options"
        }
        submitButtonStyle={payload?.SubmitButtonStyle}
        maxSelections={payload?.Score ?? multipleChoiceData.length}
      />
    );
  };
  const getWaitingComponents = () => {
    const imgSrc = payload?.Image ?? null;
    return (
      <Box
        style={{
          width: "60%",
          marginTop: "20px",
          padding: "10px",
        }}
      >
        <Text
          style={{
            width: "100%",
            textAlign: "center",
            fontSize: "24px",
            color: theme.TextColor,
          }}
        >
          {instructions}
        </Text>
        <Text
          style={{
            width: "100%",
            textAlign: "center",
            fontSize: "18px",
            color: theme.ReminderColor,
          }}
        >
          {subInstructions}
        </Text>
        {imgSrc && <img src={imgSrc} style={{ width: "100%" }} alt={""} />}
      </Box>
    );
  };
  const getComponents = () => {
    switch (state) {
      case "MultipleChoice":
        return getMultipleChoiceComponents();
      case "MultipleChoiceMultipleSelect":
        return getMultipleChoiceMultipleSelectComponents();
      case "SubmitText":
        return (
          <SubmitText
            theme={theme}
            instructions={instructions}
            subtitle={payload?.Prompt ?? ""}
            maxLength={payload?.MaxLength ?? 150}
            onSubmit={(text: string) => {
              setIsLoading(true);
              setPlayerMessage("");
              sendSocketMessage({
                Type: payload?.ResponseMessage ?? "",
                SessionId: sessionId,
                RoomCode: roomCode,
                Payload: text,
              });
            }}
            imgSrc={payload?.Image ?? null}
            buttonText={"Submit"}
            addCopySuggestion={payload?.AddCopySuggestion ?? false}
          />
        );
      case "Waiting":
        return getWaitingComponents();
      case "JoinGame":
      default:
        return (
          <JoinGame
            savedName={playerName}
            savedCode={roomCode}
            onSubmit={async (name: string, code: string) => {
              setPlayerName(name);
              setRoomCode(code);
              setIsLoading(true);
              setPlayerMessage("");
              await sendSocketMessage({
                Type: "JoinSession",
                Payload: {
                  PlayerName: name.toUpperCase(),
                  RoomCode: code.toUpperCase(),
                },
              });
            }}
          />
        );
    }
  };
  useEffect(() => {
    playerName.length > 0 && sessionStorage.setItem("PlayerName", playerName);
    roomCode.length === 4 && sessionStorage.setItem("RoomCode", roomCode);
    sessionId.length > 0 && sessionStorage.setItem("SessionId", sessionId);
  }, [playerName, roomCode, sessionId]);
  const { castConnected, hostAuthorized } = useCastSender(
    sessionId,
    sendSocketMessage,
  );
  useEffect(() => {
    if (isConnected) {
      try {
        // Load possible session data from session storage
        const name = sessionStorage.getItem("PlayerName");
        setPlayerName(name ?? "");
        const code = sessionStorage.getItem("RoomCode");
        setRoomCode(code ?? "");
        const id = sessionStorage.getItem("SessionId");
        setSessionId(id ?? "");
        if (id && id.length > 0 && code && code.length === 4) {
          const msg: GTVMessage = {
            Type: "ReconnectPlayer",
            SessionId: id,
            Payload: code,
          };
          console.log("Reconnecting to session: ", msg);
          sendSocketMessage(msg);
        } else {
          sendSocketMessage({
            Type: "RegisterPlayer",
          });
          console.log("Registering new player");
        }
        setPlayerMessage("");
        setLogoVisible(false);
      } catch (err) {
        console.error("Error during initial connection:", err);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  return (
    <Box
      style={{
        backgroundColor: theme.BackgroundColor,
        color: theme.TextColor,
        width: "100%",
        //https://stackoverflow.com/questions/37112218/css3-100vh-not-constant-in-mobile-browser
        minHeight: "calc(100dvh - 20px)",
        height: "auto",
        display: "flex",
        flexDirection: "column",
        alignContent: "center",
        justifyContent: "space-between",
      }}
    >
      <Box
        style={{
          alignContent: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {logoVisible ? (
          <Text
            style={{
              font: "48px impact",
              marginTop: "150px",
              width: "100%",
              textAlign: "center",
            }}
          >
            GameNightTV
          </Text>
        ) : (
          <Box
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              marginBottom: "20px",
            }}
          >
            {(payload?.Round ?? 0) > 0 && (
              <Text
                style={{
                  fontSize: "12px",
                  marginTop: "20px",
                  width: "100%",
                  textAlign: "center",
                  color: "gray",
                }}
              >
                {"Round " + (payload?.Round ?? 0)}
              </Text>
            )}
            {(payload?.Round ?? 0) > 0 && (
              <Text
                style={{
                  textAlign: "center",
                  fontSize: "14px",
                  marginTop: "0px",
                }}
              >
                {"Your Score: " + (payload?.Score ?? 0)}
              </Text>
            )}
            {getComponents()}
            {/*<p* TODO */}
            {/*    id="timerText"*/}
            {/*    style={{ width: '100%', display: 'none', textAlign: 'center' }}*/}
            {/*>*/}
            {/*    Time left - 00:00*/}
            {/*</p>*/}
            {playerMessage.length > 0 && <Text>{playerMessage}</Text>}
          </Box>
        )}
        {isLoading && <LoadingSpinner asOverlay={false} />}
      </Box>
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginBottom: "10px",
        }}
      >
        {isAuthenticated && (
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {!hostAuthorized && (
              <Text
                style={{
                  textDecoration: "none",
                  fontSize: "12px",
                  marginTop: "10px",
                  color: "#ffff",
                  padding: 0,
                }}
              >
                {"Cast to a nearby TV with Google Cast!"}
              </Text>
            )}
            <Box
              id="cast-button"
              style={{
                display: "none",
                marginBottom: 20,
                marginTop: 20,
                color: castConnected ? "orange" : "gray",
              }}
            />
          </Box>
        )}
        {state === "JoinGame" &&
          (isAuthenticated ? (
            <Text
              onClick={() => {
                logout();
              }}
              style={{
                textDecoration: "none",
                fontSize: "12px",
                marginTop: "10px",
                color: "#aa0000",
                cursor: "pointer",
                padding: 0,
              }}
            >
              {"Logout"}
            </Text>
          ) : (
            <Link
              to="/login"
              style={{
                textDecoration: "none",
                fontSize: "12px",
                margin: "10px",
                color: theme.TextColor,
                padding: 0,
              }}
            >
              {"Login to Cast"}
            </Link>
          ))}
        {playerName.length > 0 && (
          <Text style={{ textTransform: "uppercase" }}>{playerName}</Text>
        )}
        <Text style={{ fontSize: "12px", marginTop: "10px" }}>
          {"\u00A9 2025 GameNightTV"}
        </Text>
      </Box>
    </Box>
  );
};

export default PlayerContainer;
